import React, { useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useQuery, useMutation } from '@apollo/client'
import { toast } from 'react-toastify'

import { dataByTypename } from '../../utils/apollo-helpers'
import {
  LOGIN,
  MFA_LOGIN,
  LOGOUT,
  CUSTOMER,
  AUTHENTICATE
} from './Auth.graphql'

import { NetworkStatus } from '@apollo/client'
import {
  useIsPasswordlessAuthEnabled,
  PASSWORDLESS_LOGOUT,
  PASSWORDLESS_LOGIN,
  isPasswordlessLoggedIn,
  isLegacyAccountLoggedIn,
  hasPasswordlessAccount
} from '@local/do-secundo-passwordless-authentication'
import {
  analyticsIdentify,
  analyticsResetIdentity,
  addUserProperties
} from '@toasttab/do-secundo-analytics'
import { FF, useFlag } from '@local/do-secundo-feature-flag'

const UNVERIFIED_CUSTOMER_SOURCES = Object.freeze([
  'MANUAL_CREATE_ACCOUNT',
  'SAVE_CREDIT_CARD',
  'LOYALTY_SIGNUP',
  'CREATE_ACCOUNT_DURING_CHECKOUT',
  'MANUAL_CREATE_PASSWORDLESS_ACCOUNT'
])

const initialUnverifiedCustomer = Object.freeze({
  guid: null,
  signupSource: null
})

export const AuthContext = React.createContext({
  authenticated: false,
  error: null,
  loading: true,
  user: null,
  unverifiedCustomer: initialUnverifiedCustomer,
  authClient: null, // Authentication Library Client from MU used only for Passwordless Authentication
  setIsAuthenticated: () => {}, // call this function to cause an app rerender. Needed after calling authClient methods that change logged in/out status
  passwordlessLogin: () => {}, // client only mutation to handle passwordless login
  passwordlessLogout: () => {} // client only mutation to handle passwordless logout
})

export const useLogin = () => {
  const PASSWORDLESS_ENABLED = useIsPasswordlessAuthEnabled()
  const [login, result] = useMutation(LOGIN, {
    refetchQueries: !PASSWORDLESS_ENABLED ? [{ query: CUSTOMER }] : [],
    awaitRefetchQueries: !PASSWORDLESS_ENABLED
  })
  if (result.data) {
    const { LoginError } = dataByTypename(result.data.login)
    return {
      login,
      result: {
        ...result,
        error: LoginError || result.error
      }
    }
  }
  return {
    login,
    result
  }
}

export const sharedCustomerMutationOptions = {
  refetchQueries: [{ query: CUSTOMER }],
  awaitRefetchQueries: true,
  // Adding this property as an experiment to see if stuck in loading issue will be resolved. From https://github.com/apollographql/react-apollo/issues/170
  notifyOnNetworkStatusChange: true
}

/**
 * Query to fetch customer data and set analytics data
 * skip option is an escape hatch to prevent making a request for
 * customerData when passwordless auth kill switch is enabled an
 * @param {boolean} shouldSkipRequest
 */
export const useCustomer = () => {
  const KILLSWITCH_ENABLED = useFlag(FF.PASSWORDLESS_AUTH_KILL_SWITCH)
  const PASSWORDLESS_LOGGED_IN = isPasswordlessLoggedIn()
  const response = useQuery(CUSTOMER, {
    notifyOnNetworkStatusChange: true,
    skip:
      (KILLSWITCH_ENABLED && PASSWORDLESS_LOGGED_IN) ||
      (isLegacyAccountLoggedIn() &&
        hasPasswordlessAccount() &&
        !PASSWORDLESS_LOGGED_IN)
  })
  const trackAnalyticsIdentity = useCallback((data) => {
    if (data && data.customer && data.customer.guid) {
      analyticsIdentify(data.customer.guid)
    } else {
      analyticsResetIdentity()
    }
  }, [])

  useEffect(() => {
    response.networkStatus === NetworkStatus.ready &&
      trackAnalyticsIdentity(response?.data)
  }, [response.data, response.networkStatus, trackAnalyticsIdentity])

  return response
}

export const getStatus = (
  loginResult,
  passwordlessLoginResult,
  customerResult,
  mfaLoginResult
) => {
  const user = customerResult?.data?.customer
  if (mfaLoginResult.loading) {
    if (isPasswordlessLoggedIn()) {
      return { loading: true, mfaLoading: true, user }
    }
    return { loading: true, mfaLoading: true }
  }
  if (
    loginResult.loading ||
    passwordlessLoginResult.loading ||
    customerResult.loading ||
    mfaLoginResult.loading
  ) {
    return { loading: true }
  }

  if (customerResult.error) return { error: customerResult.error }
  if (loginResult.error) return { error: loginResult.error }
  if (passwordlessLoginResult.error) {
    return { error: passwordlessLoginResult.error }
  }
  if (mfaLoginResult.error) return { error: mfaLoginResult.error }

  if (loginResult.called) {
    const { LoginError } = dataByTypename(loginResult.data.login)
    if (LoginError) {
      return { error: LoginError }
    }
  }

  if (passwordlessLoginResult.called) {
    const { CompleteProfileCreationError } = dataByTypename(
      passwordlessLoginResult.data.createProfileCreation
    )
    if (CompleteProfileCreationError) {
      return { error: CompleteProfileCreationError }
    }
  }

  if (mfaLoginResult.called) {
    const { LoginError } = dataByTypename(mfaLoginResult.data.mfaLogin)
    if (LoginError) {
      if (isPasswordlessLoggedIn()) {
        return { error: LoginError, user }
      }
      return { error: LoginError }
    }
  }

  return { user }
}

export const AuthProvider = React.memo(({ children, ...props }) => {
  const PASSWORDLESS_ENABLED = useIsPasswordlessAuthEnabled()

  const mutationOptions = PASSWORDLESS_ENABLED
    ? null
    : sharedCustomerMutationOptions
  // unverifiedCustomer is used for loyalty signup
  const [unverifiedCustomer, setUnverifiedCustomerValue] = useState(
    initialUnverifiedCustomer
  )

  // customermgmt related mutations
  const [logout, logoutResult] = useMutation(LOGOUT, mutationOptions)
  const [login, loginResult] = useMutation(LOGIN, mutationOptions)
  const [mfaLogin, mfaLoginResult] = useMutation(MFA_LOGIN, mutationOptions)
  const [authenticate] = useMutation(AUTHENTICATE, mutationOptions)

  // passwordless related mutation
  const [passwordlessLogout] = useMutation(PASSWORDLESS_LOGOUT)
  const [passwordlessLogin, passwordlessLoginResult] = useMutation(
    PASSWORDLESS_LOGIN,
    sharedCustomerMutationOptions
  )
  const customerResult = useCustomer()

  // This is not needed for passwordless authentication and can be deleted when passwordless auth goes GA
  const [mfaState, setMfaState] = useState({
    mfaRequiresVerification: false,
    challengeToken: '',
    mfaType: '',
    maskedRecipient: ''
  })

  const { user, error, loading, mfaLoading } = getStatus(
    loginResult,
    passwordlessLoginResult,
    customerResult,
    mfaLoginResult
  )

  // setUnverifiedCustomerValue is called in CreateAccountModal/CreatePasswordlessAccountModal
  const setUnverifiedCustomer = useCallback((newState) => {
    if (!newState.guid) {
      console.error('setUnverifiedCustomer must include a guid')
    }

    if (!newState.signupSource) {
      console.error('setUnverifiedCustomer must include a signupSource')
    }

    if (!UNVERIFIED_CUSTOMER_SOURCES.includes(newState.signupSource)) {
      console.error(`invalid signupSource '${newState.signupSource}'`)
    }

    setUnverifiedCustomerValue(newState)
  }, [])

  const clearUnverifiedCustomer = useCallback(() => {
    setUnverifiedCustomerValue(initialUnverifiedCustomer)
  }, [])

  useEffect(() => {
    addUserProperties({ passwordless: PASSWORDLESS_ENABLED })
  }, [PASSWORDLESS_ENABLED])

  let context = {
    authenticated: Boolean(user),
    error,
    loading,
    login,
    mfaLogin,
    logout,
    authenticate,
    clearUnverifiedCustomer,
    setUnverifiedCustomer,
    unverifiedCustomer,
    user,
    setMfaState,
    ...mfaState
  }

  if (PASSWORDLESS_ENABLED) {
    context = {
      ...context,
      mfaLoading,
      authClient: props.authClient,
      authenticated: props.isAuthenticated,
      setIsAuthenticated: props.setIsAuthenticated,
      passwordlessLogin,
      passwordlessLogout
    }
  }

  return <AuthContext.Provider value={context}>{children}</AuthContext.Provider>
})

AuthProvider.propTypes = {
  children: PropTypes.node
}

export const useAuth = () => React.useContext(AuthContext)

export const importSuccessToast = () => {
  toast('Connection process started')
}
